import { formatDate } from '@angular/common';
import { Component, effect, EffectRef, inject, Injector, linkedSignal, OnDestroy, OnInit, Signal, ViewChild, WritableSignal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { FishTicket } from '../shared/delivery-fish-ticket.model';
import { Fishery, LocalStorageService } from '../shared/local-storage.service';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { SetErrorBlock } from '../shared/set-error-block.model';
import { TripValidator, lookupItemValidator, vesselDataValidator } from '../shared/trip-validator.model';
import { VesselData } from '../vessel-reg/vessel-data.model';
import { TrawlDelivery } from '../shared/trawl-delivery.model';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ToastrService } from 'ngx-toastr';
import { CommonFunctionService } from '../shared/common-function-service.service';
import { TrawlSet } from '../shared/trawl-set.model';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { AddTicketDialogComponent } from '../add-ticket-dialog/add-ticket-dialog.component';
import { AddVesselDialogComponent } from '../add-vessel-dialog/add-vessel-dialog.component';
import { SubmitDialogComponent } from '../submit-dialog/submit-dialog.component';
import { ErrorWarningDialogComponent } from '../error-warning-dialog/error-warning-dialog.component';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { SetSelectionDialogComponent } from '../set-selection-dialog/set-selection-dialog.component';
import { Insights } from '../shared/insights.service';


enum DeliveryMode { Current, Completed, EditCompleted, Draft };

@Component({
    selector: 'app-trawl-delivery',
    templateUrl: './trawl-delivery.component.html',
    styleUrl: './trawl-delivery.component.css',
    standalone: false
})
export class TrawlDeliveryComponent implements OnInit, OnDestroy {
  private injector = inject(Injector);
  updateEffect!: EffectRef;
  /**
   * Determines the view which can be: 0 = The current unsubmitted delivery, 1 = A completed delivery, 2 = A completed delivery being edited
   */
  viewMode: DeliveryMode = DeliveryMode.Current;
  
  /**
   * If viewing a completed delivery, index into {@link LocalStorageService.received_landings}
   */
  completedInd: number = 0;

  /**
   * When viewing a completed delivery, it can be edited and resubmitted within 7 days of its original submission date
   */
  canEdit: boolean = false;

  /**
   * The current landing/delivery
   */
  landing: TrawlDelivery = new TrawlDelivery(); 
  fishTicketColumns: string[] = ['Buyer', 'Fish Ticket Number', 'Date'];
  @ViewChild(MatTable) table!: MatTable<FishTicket>;

  previousSetIndex: number = 0;
  matToolTipText: string = "Back to Home"

  landingForm: FormGroup = new FormGroup({
    vessel: new FormControl(new VesselData('', ''), [Validators.required, vesselDataValidator()]), //Default to last vessel entered
    operator: new FormControl('',  [Validators.required]), //Default to account name
    departureDate: new FormControl( [Validators.required]), //Default to today's date
    departurePort: new FormControl(new LookupItem(''), [Validators.required, lookupItemValidator()]), //Default to last port entered
    returnDate: new FormControl( [Validators.required]), //Default to today's date
    returnPort: new FormControl(new LookupItem(''), [Validators.required, lookupItemValidator()]), //Default to last port entered
    isExemptedFP: new FormControl(false), //Default to last
    isEMTrip: new FormControl(false), //Default to last
    isObservedTrip: new FormControl(false), //Same here supposedly
    crewSize: new FormControl('', [Validators.required, Validators.min(1), Validators.max(20)]), //Default to last
    atSeaDelivery: new FormControl(false, [Validators.required]),
    mothershipName: new FormControl(''),
    comments: new FormControl('', Validators.maxLength(250))
  });

  /**
   * For populating the vessel dropdown
   */
  vesselOptions: WritableSignal<VesselData[]>;
  mothershipOptions: WritableSignal<string[]>;

  /**
   * On every change to the landing form, this subscription catches it and runs the form update procedure
   */
  landingFormSubscription: Subscription = new Subscription();

  portOptions: LookupItem[] = [];
  filteredDeparturePorts!: Observable<LookupItem[]>;
  filteredArrivalPorts!: Observable<LookupItem[]>;
  portSubscription!: Subscription;

  lastBuyer: string = '';

  selectedDeliveryMethod: string = 'shore';

  /**
   * Periodically updated from {@link TripValidator.errorWarningDialogContent}
   */
  errors: SetErrorBlock[] = [];
  onlyWarnings: boolean = false;
  /**
   * Which attached sets have errors?
   */
  validSets: Map<string, boolean> = new Map<string, boolean>();

  /**
   * When editing a completed delivery, we use this to keep track of which sets were originally submitted with the delivery
   */
  preExistingSets: Map<string, boolean> = new Map<string, boolean>();

  /**
   * The sets on this delivery, sorted
   */
  sortedSets: TrawlSet[] = [];

  landingSubscription!: Subscription;

  previousScreen: string = 'menu';


  constructor(
      public dataSync: LocalStorageService,
      public tripValidator: TripValidator,
      public router: Router,
      private route: ActivatedRoute,
      private onlineStatusService: OnlineStatusService,
      private toastr: ToastrService, 
      private insights: Insights,
      public dialog: MatDialog, 
      private lookupService: LookupDataService,
      private common: CommonFunctionService) { 
        this.vesselOptions = linkedSignal(() => dataSync.current_user().operatedVessels);
        this.mothershipOptions = linkedSignal(() => dataSync.trawl_motherships());

        if (this.viewMode == DeliveryMode.Current) {
          this.updateEffect = effect(() => {
            this.landing = this.dataSync.user_trawlTrip().trawlDelivery
            this.patchForm(false);
            this.sortSetsByDate();
            if (!this.landing.atSeaDelivery) this.table?.renderRows();
          });
        } else if (this.viewMode == DeliveryMode.EditCompleted) {
          this.updateEffect = effect(() => {
            this.landing = this.dataSync.edited_trawlLogbook();
            this.patchForm(false);
            this.sortSetsByDate();
            if (!this.landing.atSeaDelivery) this.table?.renderRows();
          })
        } else if (this.viewMode == DeliveryMode.Draft) {
          this.updateEffect = effect(() => {
              let draft = this.dataSync.trawl_drafts().find(x => x.localLandingGUID == this.landing.localLandingGUID);
              if (!draft) this.router.navigate(['trawl_drafts']);
              else {
                this.landing = draft;
                this.patchForm(false);
                this.sortSetsByDate();
                if (!this.landing.atSeaDelivery) this.table?.renderRows();
              } 
          });
        }
    }


  ngOnInit() {  
    this.previousScreen = this.route.snapshot.queryParams.previous ?? 'menu';
    this.previousSetIndex = +(this.route.snapshot.queryParams.setIndex ?? 0);

    //Completed delivery or not, passed via URL
    this.viewMode = +this.route.snapshot.paramMap.get('pending')!;

    //Set the right tooltip depending on where the back button will take user
    this.matToolTipText = this.previousScreen == 'set_detail_trawl' ? 'Back to Set' : this.viewMode == 3 ? 'Back to Drafts' : this.viewMode > 0 ? 'Back to Deliveries' : 'Back to Menu';
    this.completedInd = this.viewMode != DeliveryMode.Current ? Number.parseInt(<string>this.route.snapshot.paramMap.get('index')) : -1;

    switch (this.viewMode) {
      case DeliveryMode.Current:
        this.landing = this.dataSync.user_trawlTrip().trawlDelivery;
        this.landing.sets = this.dataSync.user_trawlTrip().sets;
        if (this.landing.isNewDelivery) {
          let last = this.dataSync.received_trawlLogbooks()[0];
          if (last) {
            this.landing.vessel = VesselData.transformAnonymous([last.vessel])[0];
            this.landing.returnPort = last.returnPort;
            if (last.tickets.length > 0) this.lastBuyer = last.tickets[last.tickets.length - 1].buyer;
          }

          this.landing.sets = this.dataSync.user_trawlTrip().sets;
          this.landing.isNewDelivery = false;

          this.dataSync.updateLanding(Fishery.Trawl, false, 'trawl').then(result => {
            if (result) console.log("New delivery updated"); else console.log("New delivery update failed");
          });

          this.landing.operatorName = `${this.dataSync.current_user().firstName} ${this.dataSync.current_user().lastName}`;
        }
        break;
      case DeliveryMode.Draft:
        this.landing = this.dataSync.trawl_drafts()[this.completedInd];
        break;
      case DeliveryMode.Completed:
        this.landing = this.dataSync.received_trawlLogbooks()[this.completedInd];
        this.canEdit = new Date(this.landing.receivedDate.valueOf() + 7*24*60*60*1000) > new Date();
        this.vesselOptions.set(VesselData.transformAnonymous([this.landing.vessel])); 
        this.landingForm.disable();
        break;
      case DeliveryMode.EditCompleted:
        this.landing = this.dataSync.edited_trawlLogbook();
        if (new Date(this.landing.receivedDate.valueOf() + 7*24*60*60*1000) < new Date()) {
          this.landing = this.dataSync.received_trawlLogbooks()[this.completedInd];
          this.discardEdits(false);
          this.toastr.warning('One week edit time window for this delivery has passed. Contact FedElog@psmfc.org if you have any questions', 'Logbook edits removed', {timeOut: 8000});            
        } else this.canEdit = true;
        break;
    }

    this.patchForm();
    
    if (this.viewMode != DeliveryMode.Completed) {     
      this.subscribeToFormUpdate();
      this.resetErrorContent();
      
      this.filteredDeparturePorts = this.landingForm.get('departurePort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );
      this.filteredArrivalPorts = this.landingForm.get('returnPort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );
    }

    this.portSubscription = this.lookupService.getTrawlPortsObservable().subscribe((value) => this.portOptions = value);     
    // sort landing sets
    this.sortSetsByDate();  
 
  }

  //Write a method to remove duplicates from the sortedSets array
  removeDuplicates() {
    const uniqueSets = new Map<string, TrawlSet>();
    this.landing.sets.forEach(set => {
      uniqueSets.set(set.fishingEventID, set);
    });
    this.landing.sets = Array.from(uniqueSets.values());
  }

  patchForm(emitEvent: boolean = true) {
    this.landingForm.patchValue({
      vessel: this.landing.vessel,
      operator: this.landing.operatorName,
      departureDate: this.landing.departureDate ? formatDate(this.landing.departureDate, 'EEEE, MMM d, y, HH:mm', 'en-US') : '',
      departurePort: this.landing.departurePort.name ? this.landing.departurePort : '',
      returnDate: this.landing.returnDate ? formatDate(this.landing.returnDate, 'EEEE, MMM d, y, HH:mm', 'en-US') : '',
      returnPort: this.landing.returnPort.name ? this.landing.returnPort : '',
      isExemptedFP: this.landing.isEFP,
      isEMTrip: this.landing.isEMTrip,
      isObservedTrip: this.landing.isObservedTrip,
      crewSize: this.landing.crewSize,
      atSeaDelivery: this.landing.atSeaDelivery,
      mothershipName: this.landing.mothershipName,
      comments: this.landing.comments
    }, {emitEvent: emitEvent});
  }

  /**
   * Sort the sets by date first then by their set numbers.
   */
  sortSetsByDate(): void {

    var compareSetNumbers = (a: string, b: string) => { return !b ? -1 : !a ? 1 : a.localeCompare(b); }
    var compareSetDates = (aString: string, bString: string, aNumber: string, bNumber: string) => {
      return (aString < bString) ? -1 : (aString > bString) ? 1 : compareSetNumbers(aNumber, bNumber); }

    this.landing.sets.sort((a, b) => {

      // start sorting, check if dates are null first
      return (a.startDate && !b.startDate) ? -1 : 
      (!a.startDate && b.startDate) ? 1 : 
      (!a.startDate && !b.startDate) ? compareSetNumbers(a.setNumber, b.setNumber) : 
      compareSetDates(new Date(a.startDate).toISOString(), new Date(b.startDate).toISOString(), a.setNumber, b.setNumber); 
    });

    this.sortedSets = this.landing.sets;
    
  }

  /**
   * We update the landing in the database and the current landing object on every form update. 
   */
    subscribeToFormUpdate() {
      this.landingFormSubscription = this.landingForm.valueChanges.subscribe(value => {
        if (!value.vessel) {
          value.vessel = this.landing.vessel;
          this.landingForm.get('vessel')!.setValue(value.vessel, {onlySelf: true, emitEvent: false});
        }

        (this.landing as TrawlDelivery).updateValue(value);                           
        this.dataSync.updateLanding(Fishery.Trawl, this.viewMode == DeliveryMode.EditCompleted, 'trawl', this.landing.localLandingGUID, this.viewMode == DeliveryMode.Draft).then(res => {
          if (!res.success) {
            this.toastr.error('Error updating delivery', 'Error', {timeOut: 5000});
            this.insights.trackException(res.error);
          }
          this.resetErrorContent()
        } );
      });
    }
  

    onDeliveryMethodChange(method: string) {
      if (this.viewMode == DeliveryMode.Completed) return;
      this.selectedDeliveryMethod = method;
      if (this.selectedDeliveryMethod == 'shore') {
        this.landingForm.get('mothershipName')?.setValue('');
        this.landingForm.get('atSeaDelivery')?.setValue(false);
        this.landingForm.get('mothershipName')?.clearValidators();
      } else {
        this.landingForm.get('mothershipName')?.setValidators([Validators.required]);
        this.landingForm.get('atSeaDelivery')?.setValue(true);
        //Remember to clear any fish ticket warnings/errors
      }
    }
  
    compareFn(l1: LookupItem, l2: LookupItem): boolean {
      return l1 && l2 ? l1.code === l2.code : l1 === l2;
    }
  
    compareVessel(v1: VesselData, v2: VesselData): boolean {
      return v1 && v2 ? v1.vesselName+v1.vesselCoastNumber === v2.vesselName+v2.vesselCoastNumber : v1 === v2;
    }
  
    /**
     * Go get all errors/warnings for the delivery and its sets
     */
    resetErrorContent() {
      this.errors = this.tripValidator.errorWarningDialogContentTrawl(this.viewMode == DeliveryMode.EditCompleted, this.viewMode == DeliveryMode.Draft ? this.completedInd : -1);
      this.onlyWarnings = !this.errors.some(x => x.isError) == true;
      this.validSets.clear();
      this.preExistingSets.clear();
      this.landing.sets.forEach(set => {
        this.validSets.set(set.fishingEventID, this.errors.find(x => x.setID == set.fishingEventID && x.isError) == undefined ? true : false);
        this.preExistingSets.set(set.fishingEventID, this.dataSync.user_trawlTrip().sets.findIndex((ts: TrawlSet) => ts.fishingEventID == set.fishingEventID) == -1);
      });
    }
  
  
    /**
     * As in settings we can add a new vessel here.
     */
    addVessel() {
      this.common.addVessel(-1, false).then((newVessel: VesselData) => {
        this.landingForm.get('vessel')?.setValue(newVessel);
      }).catch((err: any) => console.log(err))
    }

    addMothership() {

      const dialogRef = this.dialog.open(AddVesselDialogComponent, {
        width: '425px',
        data: {
          mothership: true
        }
      });
  
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.dataSync.addAuxiliaryData(result.vesselName, 'mothership_names').then(res => {
            if (res.success) {
              this.landing.mothershipName = result.vesselName;
              this.landingForm.get('mothershipName')?.setValue(result.vesselName);
            } else {
              this.toastr.error('Error adding mothership name to the list', 'Error', {timeOut: 5000});
              this.insights.trackException(res.error);
            }
            
          });
        } else {
          this.landingForm.get('mothershipName')?.setValue(result.vesselName);
        }
      });
    }

    /**
     * Display errors/warnings to the user if there are any. Otherwise we go to the summary-review component.
     */
    openSubmitDialog() {
      if (this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE) {
        this.dialog.open(SubmitDialogComponent, {
          autoFocus: false,
          width: '400px',
          height: '450px',
          data: this.errors.filter(x => x.isError).length > 0
        });
  
      } else {
        this.openErrorDialog(true);
      }
    }
      
  
    removeSet(index: number) {
      //Editing?
      this.landing.sets.splice(index, 1);
      this.dataSync.updateLanding(Fishery.Trawl, this.viewMode == DeliveryMode.EditCompleted, 'trawl', this.landing.localLandingGUID, this.viewMode == DeliveryMode.Draft).then(res => {
        if (res.success) this.resetErrorContent(); 
        else {
          this.toastr.error('Error removing set from delivery', 'Error', {timeOut: 5000});
          this.insights.trackException(res.error);
        }
      });
    }
  

    openErrorDialog(submission: boolean = false) {
        this.resetErrorContent();

        if (this.errors.length == 0 && submission) {
          this.router.navigate(['summary_review', 'Trawl'], { queryParams: { previous: this.previousScreen, draftIndex: this.viewMode == DeliveryMode.Draft ? this.completedInd : -1, deliveryIndex: this.viewMode == DeliveryMode.EditCompleted ? this.completedInd : -1 } });
          return;      
        }

        //Launch dialog with collected results
        const dialogRef = this.dialog.open(ErrorWarningDialogComponent, {
          autoFocus: false,
          width: (Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) - 10).toString() + 'px',
          height: (Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0) - 10).toString() + 'px',
          data: { errors: this.errors, submission: submission }
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result != undefined) {
            if (submission && result == -2) {
              //Go to summary review
              this.router.navigate(['summary_review', 'Trawl'], { queryParams: { previous: this.previousScreen, 
                deliveryIndex: this.viewMode == DeliveryMode.EditCompleted ? this.completedInd : -1, draftIndex: this.viewMode == DeliveryMode.Draft ? this.completedInd : -1 } });
            } else if (result != -1) {
              // result is the fishingEventID, need to find the index of the set
              const setIndex = this.landing.sets.findIndex(s => s.fishingEventID === result);
              if (this.viewMode == DeliveryMode.EditCompleted)
                this.router.navigate(['set_detail_trawl', -1, false], { queryParams: { editedIndex: result } });
              else
                this.router.navigate(['set_detail_trawl', setIndex, false], { queryParams: { previous: 'delivery' } });
              
              if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
            }
          }
        });
      }


    editDateTime(from: string) {
        if (this.viewMode == DeliveryMode.Completed) return;
        // Use the model's stored Date object which preserves time; form value is a formatted string
        let dateInput: Date;
        if (from === 'departureDate') {
          dateInput = this.landing.departureDate && this.landing.departureDate instanceof Date ? this.landing.departureDate : new Date();
        } else if (from === 'returnDate') {
          dateInput = this.landing.returnDate && this.landing.returnDate instanceof Date ? this.landing.returnDate : new Date();
        } else {
          dateInput = new Date();
        }
    
        const dialogRef = this.dialog.open(DateTimePickerComponent, {
          width: '500px',
          data: { date: dateInput, minDate: from == 'returnDate' ? new Date() : this.landingForm.get('departureDate')?.value, isDelivery: true, isTrawl: true, hmsField: from },
        });
    
        dialogRef.afterClosed().subscribe(result => {
          if (result) this.landingForm.get(from)?.setValue(formatDate(result, 'EEEE, MMM d, y, HH:mm', 'en-US'));
        });
      }


    /**
     * Adding ar editing a fishing ticket
     * @param index Index into landing tickets array, if we are editing an existing ticket
     * @returns 
     */
    openFishTicketDialog(index: number) {
      if (this.viewMode == DeliveryMode.Completed) return;
      if (index >= this.landing.tickets.length) {
        this.landing.tickets.push(new FishTicket('', ''));
        this.landing.tickets[index].deliveryDate = this.landingForm.get('deliverDate')?.value == '' ? new Date() : new Date(this.landingForm.get('deliverDate')?.value);
        this.landing.tickets[index].buyer = this.lastBuyer;
      }
  
      let usedNumbers: string[] = [];
      this.landing.tickets.forEach(tck => {if (this.landing.tickets[index].ticketNumber != tck.ticketNumber) usedNumbers.push(tck.ticketNumber)});
  
      const dialogRef = this.dialog.open(AddTicketDialogComponent, {
        autoFocus: false,
        width: '400px',
        position: {top: '15px'},
        disableClose: true,
        data: {tckt: this.landing.tickets[index], usedNums: usedNumbers, returnDate: this.landing.returnDate}
      });
  
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (result.delete) {
            this.landing.tickets.splice(index, 1);
          } else {
            this.landing.tickets[index].buyer = result.buyer;
            this.landing.tickets[index].ticketNumber = result.number;
            this.landing.tickets[index].deliveryDate = result.date;
          }
          if (!this.landing.atSeaDelivery) this.table.renderRows();
          this.dataSync.updateLanding(Fishery.Trawl, this.viewMode == DeliveryMode.EditCompleted, 'trawl', this.landing.localLandingGUID, this.viewMode == DeliveryMode.Draft).then(res => {
            if (!res.success){
              this.toastr.error('Error adding ticket to delivery', 'Error', {timeOut: 5000});
              this.insights.trackException(res.error);
            }
          });
        } else {
          this.landing.tickets.splice(index, 1);
        }
        this.resetErrorContent();
      });
  }
  
    /**
     * For adding/removing sets attached to the delivery
     * @returns 
     */
    openSetSelection() {
      // Return early if viewing a completed delivery.
      if (this.viewMode === DeliveryMode.Completed) return;
    
      if (this.viewMode === DeliveryMode.Draft) {
        // Sets already in the draft landing.
        const draftSetIDs: string[] = this.landing.sets.map(s => s.fishingEventID);
        // Sets from the active trip.
        const currentTripSets: TrawlSet[] = this.dataSync.user_trawlTrip().sets;        
        // Create a union: include all sets from both sources.
        const unionMap = new Map<string, TrawlSet>();
        currentTripSets.forEach(s => unionMap.set(s.fishingEventID, s));
        this.landing.sets.forEach(s => unionMap.set(s.fishingEventID, s));

        const originalTrawlUnion: TrawlSet[] = Array.from(unionMap.values());
        
        const dialogRef = this.dialog.open(SetSelectionDialogComponent, {
          width: '400px',
          height: '600px',
          data: {
            data: draftSetIDs,
            combinedTrawlSets: originalTrawlUnion,
            fishery: 'Trawl',
            editedDelivery: false,
            isTrawlDraft: true
          }
        });
        
        dialogRef.afterClosed().subscribe((result: TrawlSet[]) => {
          if (result) {
            // Update the draft landing's sets with the selected result.
            this.landing.sets = result;
            // First update the draft landing
            this.dataSync.updateLanding(Fishery.Trawl, false, 'trawl', this.landing.localLandingGUID, true).then(res => {
              if (res.success) {
                // Calculate updated active trip sets: original union minus the draft sets.
                const newDraftIDs = new Set(this.landing.sets.map(s => s.fishingEventID));
                this.dataSync.user_trawlTrip.update(trip => {
                  trip.sets = originalTrawlUnion.filter((set: TrawlSet) => !newDraftIDs.has(set.fishingEventID));
                  return trip;
                });
                // Save active trip updates by calling updateTrip.
                this.dataSync.updateTrip('trawl').then(res => {
                  if (res.success) {
                    this.resetErrorContent();
                    this.sortSetsByDate();
                  } else {
                    this.toastr.error('Error updating trip sets', 'Error', {timeOut: 5000});
                    this.insights.trackException(res.error);
                  }
                  
                });
              } else {
                this.toastr.error('Error updating draft landing', 'Error', {timeOut: 5000});
                this.insights.trackException(res.error);
              }
              
            });
          }
        });
      } else {
        // Non-draft behavior.
        let ids: string[] = this.landing.sets.map(s => s.fishingEventID);
        const dialogRef = this.dialog.open(SetSelectionDialogComponent, {
          width: '400px',
          height: '600px',
          data: {
            data: ids,
            editedDelivery: this.viewMode === DeliveryMode.EditCompleted,
            fishery: 'Trawl'
          }
        });

        dialogRef.afterClosed().subscribe((result: TrawlSet[]) => {
          if (result) {
            this.landing.sets = this.viewMode === DeliveryMode.EditCompleted ?
              this.landing.sets.filter((fe: TrawlSet) => this.preExistingSets.get(fe.fishingEventID)).concat(result) :
              result;
            this.dataSync.updateLanding(Fishery.Trawl, this.viewMode == DeliveryMode.EditCompleted, 'trawl', this.landing.localLandingGUID, this.viewMode == DeliveryMode.Draft).then(res => {
              if (res.success) {
                this.resetErrorContent();
                this.sortSetsByDate();
              } else {
                this.toastr.error('Error updating delivery', 'Error', {timeOut: 5000});
                this.insights.trackException(res.error);
              }
              
            });
          }
        });
      }
    }
  
    /**
     * Clicking on a set attached to delivery takes us to that set
     * @param setID fishingEventID of set to go to
     * @returns 
     */
  goToSet(setID: string) {
      //Editing?
    if (this.viewMode == DeliveryMode.Completed) {
      // Find the set index from the landing sets.
      const setIndex = this.landing.sets.findIndex(x => x.fishingEventID === setID);
      if (setIndex !== -1) {
        // Navigate to the set page and pass a parameter that indicates "readOnly" mode.
        this.router.navigate(['set_detail_trawl', setIndex, false], { queryParams: { previous: 'deliveries_list', completedIndex: this.completedInd, readOnly: true } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) {
          window.scrollTo(0, 0);
        }
      }
    } else {
      let setIndex = this.dataSync.user_trawlTrip().sets.findIndex(x => x.fishingEventID === setID);
      if (setIndex != -1) {
        this.router.navigate(['set_detail_trawl', setIndex, false], { queryParams: { previous: 'delivery' } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
      } else if (this.viewMode == DeliveryMode.EditCompleted) {
        //The set no longer exists on device and is saved as part of this delivery rather than the user sets
        this.router.navigate(['set_detail_trawl', -1, false], { queryParams: { editedIndex: this.landing.sets.findIndex(x => x.fishingEventID === setID) } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
      } else if (this.viewMode == DeliveryMode.Draft) {
        this.router.navigate(['set_detail_trawl', -1, false], { queryParams: { draftIndex: this.completedInd, draftSetId: setID } });
        if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) window.scrollTo(0, 0);
      }
    }
  
  }
    

  enableEdits() {
    //Check for edits in progress on another delivery
    let logbook = this.dataSync.edited_trawlLogbook();
    if (logbook.logbookId && logbook.logbookId != this.landing.logbookId) {
      this.toastr.warning(`Logbook ${logbook.logbookId.substring(17)} has unsubmitted edits. Submit or discard those to edit this logbook`, 
      'Multiple edited logbooks');
      return;
    }

    this.dataSync.editExistingTrawlLanding(this.landing.logbookId).then(_ => {
      this.viewMode = DeliveryMode.EditCompleted;
      this.router.navigate(['trawl_delivery', 2, this.completedInd], { queryParams: { previous: 'deliveries_list' } });
      this.landing = this.dataSync.edited_trawlLogbook();

      this.landingForm.enable();

      this.subscribeToFormUpdate();
      this.resetErrorContent();
      this.filteredDeparturePorts = this.landingForm.get('departurePort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );
      this.filteredArrivalPorts = this.landingForm.get('returnPort')!.valueChanges.pipe(
        startWith(''),
        map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );

      this.updateEffect = effect(() => {
        this.landing = this.dataSync.edited_trawlLogbook();
        this.patchForm();
        this.sortSetsByDate();
        if (!this.landing.atSeaDelivery) this.table.renderRows();
      }, {injector: this.injector});
    })

  }


  discardEdits(promptUser: boolean) {
    if (this.updateEffect) this.updateEffect.destroy();
    //Lots of warnings and user confirmation before going forward
    let discard = () => {
      this.viewMode = DeliveryMode.Completed;

      this.landingSubscription?.unsubscribe(); 
      this.landingFormSubscription?.unsubscribe();
      this.landingForm.disable();

      this.dataSync.discardLogbookEdits('trawl');

      //Unassign LocalStorageService.edited_landing, revert this landing to completed delivery landing
      this.landing = this.dataSync.received_trawlLogbooks()[this.completedInd] as TrawlDelivery;
      this.vesselOptions.set(VesselData.transformAnonymous([this.dataSync.received_trawlLogbooks()[this.completedInd].vessel]));
      this.landingForm.patchValue({
        vessel: this.dataSync.received_trawlLogbooks()[this.completedInd].vessel,
        operator: this.dataSync.received_trawlLogbooks()[this.completedInd].operatorName,
        crewSize: this.landing.crewSize,
        departureDate: this.landing.departureDate ? formatDate(this.landing.departureDate, 'fullDate', 'en-US') : '',
        departurePort: this.landing.departurePort.name ? this.landing.departurePort : '',
        returnDate: this.landing.returnDate ? formatDate(this.landing.returnDate, 'fullDate', 'en-US') : '',
        returnPort: this.landing.returnPort.name ? this.landing.returnPort : '',
        isExemptedFP: this.landing.isEFP,
        isEMTrip: this.landing.isEMTrip,
        isObservedTrip: this.landing.isObservedTrip
      });
      this.sortSetsByDate();
    };

    if (promptUser) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {screen: 'discardedits'}
      }).afterClosed().subscribe(result => {
        if (!result) return;
        discard();  
      });  
    } else discard();
    
  }

  navigateBack() {
    if (this.previousScreen == 'set_detail_trawl') this.router.navigate(['set_detail_trawl', this.previousSetIndex, false]);
    else this.router.navigate([this.previousScreen]);
  }

_forbidNonNumeric(control: AbstractControl) {
    control.setValue(control.value.replace(/[^0-9]/g, ''), {onlySelf: true, emitEvent: false});
  }

  ngOnDestroy() {
    if (this.viewMode != DeliveryMode.Completed) {
      if (this.landingFormSubscription) this.landingFormSubscription.unsubscribe();
      if (this.portSubscription) this.portSubscription.unsubscribe();
      if (this.landingSubscription) this.landingSubscription.unsubscribe(); 
    }   
  }

}
